import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { Close, GetApp, IosShare } from '@mui/icons-material';

const DISMISS_KEY = 'pd_install_prompt_dismissed_at';
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const ua = window.navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    if (isIOS && isSafari) {
      setShowIosHelp(true);
      setVisible(true);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') setVisible(false);
  };

  if (!visible) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 1400,
        p: 1.5,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        maxWidth: 480,
        mx: 'auto'
      }}
    >
      <Box component="img" src="/icon-192.png" alt="" sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Install Prescription Drives
        </Typography>
        {showIosHelp && !deferredPrompt ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
            Tap <IosShare sx={{ fontSize: 14, verticalAlign: 'text-bottom' }} /> Share, then “Add to Home Screen”.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Open it from your home screen like an app.
          </Typography>
        )}
      </Box>
      {deferredPrompt && (
        <Button variant="contained" size="small" startIcon={<GetApp />} onClick={install}>
          Install
        </Button>
      )}
      <IconButton size="small" onClick={dismiss} aria-label="Dismiss">
        <Close fontSize="small" />
      </IconButton>
    </Paper>
  );
};

export default InstallAppPrompt;
